/**
 * SHO-8958: vroege afhandeling van declarative `on:click` vóór custom-element upgrade.
 *
 * Alleen queue/replay als de host nog niet klaar is. Zodra de host geüpgraded is met
 * de methode, doet dit script niets — `component.js` handelt af.
 *
 * Bij replay worden path-/query-args meegenomen (zoals component.js), anders falen
 * o.a. `/onLineItemRemove/1` stil.
 */

const ON_CLICK_ATTR = 'on:click';

/** @type {boolean} */
let listenersRegistered = false;

/**
 * @param {Event} event
 * @returns {Element | null}
 */
function getOnClickElement(event) {
  const target = event.composedPath?.()[0] ?? event.target;
  if (!(target instanceof Element)) return null;
  if (target.hasAttribute(ON_CLICK_ATTR)) return target;
  return target.closest('[on\\:click]');
}

/**
 * Parse `on:click` value the same way as component.js.
 * @param {string} value
 * @returns {{ selector: string, method: string, data: string | null }}
 */
function parseOnClickValue(value) {
  let [selector, method] = value.split('/');
  const matches = value.match(/([\/\?][^\/\?]+)([\/\?][^\/\?]+)$/);
  const data = matches ? matches[2] : null;
  method = (method || '').replace(/\?.*/, '');
  return { selector: selector || '', method, data };
}

/**
 * Mirrors component.js parseValue.
 * @param {string} str
 * @returns {string | number | boolean}
 */
function parseValue(str) {
  if (str === 'true') return true;
  if (str === 'false') return false;

  const maybeNumber = Number(str);
  if (!Number.isNaN(maybeNumber) && str.trim() !== '') return maybeNumber;

  return str;
}

/**
 * Mirrors component.js parseData — `?a=1` → object, `/1` → number/string.
 * @param {string} str
 * @returns {Object | string | number | boolean}
 */
function parseData(str) {
  const delimiter = str[0];
  const data = str.slice(1);

  return delimiter === '?'
    ? Object.fromEntries(
        Array.from(new URLSearchParams(data).entries()).map(([key, value]) => [key, parseValue(value)])
      )
    : parseValue(data);
}

/**
 * Resolve the host for an empty or explicit `on:click` selector.
 * Supports non-`-component` hosts (header-drawer, zoom-dialog) and skips utility CEs
 * without the method (scroll-hint — SHO-9002).
 *
 * @param {Element} element
 * @param {string} selector
 * @param {string} [method]
 * @returns {Element | null}
 */
function resolveInstance(element, selector, method = '') {
  if (selector) {
    if (selector.startsWith('#')) {
      return document.querySelector(selector);
    }
    return element.closest(selector);
  }

  /** @type {Element | null} */
  let pendingHost = null;

  let node = /** @type {Node | null} */ (element);
  while (node) {
    if (node instanceof HTMLElement) {
      const tag = node.tagName.toLowerCase();
      if (tag.includes('-')) {
        const ctor = customElements.get(tag);

        if (tag.endsWith('-component')) {
          if (!method) return node;
          if (ctor && node instanceof ctor) {
            if (typeof /** @type {any} */ (node)[method] === 'function') return node;
          } else if (!pendingHost) {
            pendingHost = node;
          }
        } else if (ctor) {
          if (node instanceof ctor) {
            if (method && typeof /** @type {any} */ (node)[method] === 'function') {
              return node;
            }
          } else if (!pendingHost) {
            pendingHost = node;
          }
        } else if (!pendingHost) {
          pendingHost = node;
        }
      }
    }
    const parent = node.parentNode;
    if (parent) {
      node = parent;
      continue;
    }
    const root = node.getRootNode();
    node = root instanceof ShadowRoot ? root.host : null;
  }

  return pendingHost;
}

/**
 * @param {Element} el
 * @returns {boolean}
 */
function isUpgradedHost(el) {
  const ctor = customElements.get(el.localName);
  return Boolean(ctor && el instanceof ctor);
}

/**
 * @param {Element} trigger
 * @returns {{ testId: string | null, ariaLabel: string | null, onClick: string, tagName: string }}
 */
function describeTrigger(trigger) {
  return {
    testId: trigger.getAttribute('data-testid'),
    ariaLabel: trigger.getAttribute('aria-label'),
    onClick: trigger.getAttribute(ON_CLICK_ATTR) || '',
    tagName: trigger.tagName.toLowerCase(),
  };
}

/**
 * @param {ReturnType<typeof describeTrigger>} desc
 * @returns {Element | null}
 */
function findLiveTrigger(desc) {
  if (desc.testId) {
    const byTestId = document.querySelector(`[data-testid="${CSS.escape(desc.testId)}"]`);
    if (byTestId) return byTestId;
  }
  if (desc.ariaLabel) {
    const byLabel = document.querySelector(
      `${desc.tagName}[aria-label="${CSS.escape(desc.ariaLabel)}"][on\\:click]`
    );
    if (byLabel) return byLabel;
  }
  if (desc.onClick) {
    const candidates = document.querySelectorAll(`${desc.tagName}[on\\:click]`);
    for (const el of candidates) {
      if (el.getAttribute(ON_CLICK_ATTR) === desc.onClick) return el;
    }
  }
  return null;
}

/**
 * @param {Element} instance
 * @param {string} method
 * @param {Event} event
 * @param {Element} trigger
 * @param {string | null} [data]
 */
function invokeMethod(instance, method, event, trigger, data = null) {
  const callback = /** @type {any} */ (instance)[method];
  if (typeof callback !== 'function') return false;

  const proxiedEvent =
    event.target !== trigger
      ? new Proxy(event, {
          get(target, property) {
            if (property === 'target') return trigger;
            const value = Reflect.get(target, property);
            return typeof value === 'function' ? value.bind(target) : value;
          },
        })
      : event;

  /** @type {(Event | ReturnType<typeof parseData>)[]} */
  const args = [proxiedEvent];
  if (data) args.unshift(parseData(data));

  callback.call(instance, ...args);
  return true;
}

/**
 * @param {Element} host
 * @returns {Element | null}
 */
function findLiveHost(host) {
  if (host.isConnected) return host;
  if (host instanceof HTMLElement && host.id) {
    const byId = document.getElementById(host.id);
    if (byId) return byId;
  }
  return document.querySelector(host.localName);
}

/**
 * @param {Element} host
 * @param {() => void} run
 */
function whenHostReady(host, run) {
  const tag = host.localName;

  const attempt = () => {
    const liveHost = findLiveHost(host);
    if (liveHost && isUpgradedHost(liveHost)) {
      run();
      return true;
    }
    return false;
  };

  if (attempt()) return;

  customElements.whenDefined(tag).then(() => {
    if (attempt()) return;
    let tries = 0;
    const tick = () => {
      if (attempt() || tries++ > 60) return;
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  });
}

/**
 * @param {Element} trigger
 * @returns {boolean}
 */
function isSubmitControl(trigger) {
  return (
    (trigger instanceof HTMLButtonElement || trigger instanceof HTMLInputElement) && trigger.type === 'submit'
  );
}

/**
 * @param {Event} event
 */
function handleEarlyClick(event) {
  if (!(event instanceof Event)) return;
  if (event.defaultPrevented) return;

  const trigger = getOnClickElement(event);
  if (!trigger) return;

  const raw = trigger.getAttribute(ON_CLICK_ATTR);
  if (!raw) return;

  const { selector, method } = parseOnClickValue(raw);
  if (!method) return;

  const instance = resolveInstance(trigger, selector, method);
  if (!(instance instanceof Element)) return;

  // Host al klaar → met rust laten; component.js handelt af.
  if (isUpgradedHost(instance) && typeof /** @type {any} */ (instance)[method] === 'function') {
    return;
  }

  // Submit-knoppen (ATC) nooit queue'en — native form-submit mag niet verdwijnen.
  if (isSubmitControl(trigger)) return;

  event.preventDefault();
  event.stopImmediatePropagation();

  const desc = describeTrigger(trigger);

  whenHostReady(instance, () => {
    const liveTrigger = trigger.isConnected ? trigger : findLiveTrigger(desc);
    if (!liveTrigger) return;

    const {
      selector: liveSelector,
      method: liveMethod,
      data: liveData,
    } = parseOnClickValue(liveTrigger.getAttribute(ON_CLICK_ATTR) || raw);
    if (!liveMethod) return;

    let liveInstance = resolveInstance(liveTrigger, liveSelector, liveMethod);
    if (!(liveInstance instanceof Element) || !isUpgradedHost(liveInstance)) {
      const upgraded = findLiveHost(instance);
      if (upgraded && isUpgradedHost(upgraded)) liveInstance = upgraded;
    }
    if (!(liveInstance instanceof Element) || !isUpgradedHost(liveInstance)) return;

    invokeMethod(liveInstance, liveMethod, event, liveTrigger, liveData);
  });
}

function registerEarlyInteractionListeners() {
  if (listenersRegistered) return;
  listenersRegistered = true;
  document.addEventListener('click', handleEarlyClick, { capture: true });
}

registerEarlyInteractionListeners();

export {
  describeTrigger,
  findLiveHost,
  findLiveTrigger,
  getOnClickElement,
  handleEarlyClick,
  isUpgradedHost,
  parseData,
  parseOnClickValue,
  registerEarlyInteractionListeners,
  resolveInstance,
};
